import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Outlet, Navigate, useLocation } from 'react-router';
import './App.css';
import TopBar from './components/TopBar';
import RoleGuard from './components/RoleGuard';
import ErrorBoundary from './components/ErrorBoundary';
import { logFrontendError } from './services/api';
import { PageContextProvider } from './context/PageContext';

// Route-level code-splitting with React.lazy
const Dashboard = lazy(() => import('./pages/Dashboard'));
const ViewHotLeads = lazy(() => import('./features/ViewHotLeads'));
const ViewHitlLeads = lazy(() => import('./features/ViewHitlLeads'));
const ViewHighValueProspects = lazy(() => import('./features/ViewHighValueProspects'));
const ViewInProgressLeads = lazy(() => import('./features/ViewInProgress'));
const ViewDropoutLeads = lazy(() => import('./features/ViewDropoutLeads'));
const LeadDetails = lazy(() => import('./features/LeadDetails'));
const LeadChat = lazy(() => import('./pages/LeadChat'));
const LeadChatV2 = lazy(() => import('./pages/LeadChatV2'));
const SignIn = lazy(() => import('./pages/SignIn'));
const SignUp = lazy(() => import('./pages/SignUp'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const ChangePassword = lazy(() => import('./pages/ChangePassword'));
const Products = lazy(() => import('./pages/Products'));
const Reports = lazy(() => import('./pages/Reports'));
const ContractManagement = lazy(() => import('./pages/ContractManagement'));
const AdminSettings = lazy(() => import('./pages/AdminSettings'));
const IntentScoringTestPage = lazy(() => import('./pages/test/IntentScoringTestPage'));
const NourishingTestPage = lazy(() => import('./pages/test/NourishingTestPage'));
const MessageProcessorV2TestPage = lazy(() => import('./pages/test/MessageProcessorV2TestPage'));
const TTDLearningTestPage = lazy(() => import('./pages/test/TTDLearningTestPage'));
const KnowledgeRetrievalTestPage = lazy(() => import('./pages/test/KnowledgeRetrievalTestPage'));
const ChatbotWidget = lazy(() => import('./components/ChatbotWidget'));

const RouteFallback = () => (
  <div className="app-route-fallback-loader">
    <div className="app-route-spinner" />
    <span className="font-14">Loading...</span>
  </div>
);

const HomeIframe = () => (
  <div className="home-iframe-container">
    <iframe
      title="TripNour Home"
      src="https://tripnour-ai.lovable.app/"
      className="home-iframe"
      sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
    />
  </div>
);

// Layout wrapper for protected routes (includes TopBar & checks for auth)
const MainLayout = () => {
  const token = localStorage.getItem('access_token');
  const location = useLocation();

  if (!token) {
    return <Navigate to="/signin" replace />;
  }

  // Show ChatbotWidget on all pages except dashboard and contracts
  const showChatbotWidget = !location.pathname.startsWith('/dashboard') && 
                            !location.pathname.startsWith('/contracts') &&
                            location.pathname !== '/';

  return (
    <PageContextProvider>
      <div className="app-container">
        <TopBar />
        <Outlet />
        {showChatbotWidget && (
          <Suspense fallback={<div className="chatbot-loading" />}>
            <ChatbotWidget />
          </Suspense>
        )}
      </div>
    </PageContextProvider>
  );
};

function App() {
  React.useEffect(() => {
    const handleGlobalError = (event) => {
      const orgId = localStorage.getItem('org_id') || '';
      const username = localStorage.getItem('username') || '';
      logFrontendError({
        org_id: orgId,
        username: username,
        error_message: event.message || 'Global Error',
        stack_trace: event.error?.stack || '',
      });
    };

    const handleUnhandledRejection = (event) => {
      const orgId = localStorage.getItem('org_id') || '';
      const username = localStorage.getItem('username') || '';
      let errorMsg = 'Unhandled Promise Rejection';
      let stack = '';
      if (event.reason instanceof Error) {
        errorMsg = event.reason.message;
        stack = event.reason.stack;
      } else if (typeof event.reason === 'string') {
        errorMsg = event.reason;
      }

      logFrontendError({
        org_id: orgId,
        username: username,
        error_message: errorMsg,
        stack_trace: stack || '',
      });
    };

    window.addEventListener('error', handleGlobalError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('error', handleGlobalError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  return (
    <ErrorBoundary>
      <Router>
        <Routes>
          <Route path="/home" element={<HomeIframe />} />
          {/* Authentication routes */}
          <Route path="/signin" element={<SignIn />} />
          <Route path="/signup" element={<SignUp />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          {/* Chat route */}
          <Route path="/chat" element={<LeadChat />} />
          <Route path="/chat/:id" element={<LeadChat />} />
          <Route path="/chat/:id/:sessionId" element={<LeadChat />} />
          {/* Chat route v2 */}
          <Route path="/chatv2" element={<LeadChatV2 />} />
          <Route path="/chatv2/:id" element={<LeadChatV2 />} />
          <Route path="/chatv2/:id/:sessionId" element={<LeadChatV2 />} />
          {/* Intent Scoring Test Route */}
          <Route path="/test/IntentScoring" element={<IntentScoringTestPage />} />
          {/* Nourishing Framework Test Route */}
          <Route path="/test/NourishingFramework" element={<NourishingTestPage />} />
          {/* MessageProcessorV2 Test Route */}
          <Route path="/test/MessageProcessorV2" element={<MessageProcessorV2TestPage />} />
          {/* TTD Learning Matrix Test Route */}
          <Route path="/test/TTDLearning" element={<TTDLearningTestPage />} />
          {/* Knowledge Retrieval Engine Test Route */}
          <Route path="/test/KnowledgeRetrieval" element={<KnowledgeRetrievalTestPage />} />
          {/* Protected routes with layout */}
          <Route element={<MainLayout />}>
            <Route path="/" element={<Dashboard />} />
            <Route path="/hot-leads" element={<ViewHotLeads />} />
            <Route path="/hitl-leads" element={<ViewHitlLeads />} />
            <Route path="/high-value-prospects" element={<ViewHighValueProspects />} />
            <Route path="/in-progress" element={<ViewInProgressLeads />} />
            <Route path="/dropout-leads" element={<ViewDropoutLeads />} />
            <Route path="/lead-details/:leadId" element={<LeadDetails />} />
            <Route path="/products" element={<Products />} />
            <Route path="/contracts" element={<ContractManagement />} />
            <Route path="/reports" element={<Reports />} />
            <Route path="/change-password" element={<ChangePassword />} />
            <Route
              path="/settings"
              element={
                <RoleGuard allowedRoles={['ADMIN', 'Super Admin']}>
                  <AdminSettings />
                </RoleGuard>
              }
            />
            <Route
              path="/admin/settings"
              element={
                <RoleGuard allowedRoles={['ADMIN', 'Super Admin']}>
                  <AdminSettings />
                </RoleGuard>
              }
            />
          </Route>
        </Routes>
      </Router>
    </ErrorBoundary>
  );
}

export default App;
